import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import puzzleEventContent from '../data/puzzleEventContent.json';
import '../css/mysteryEventSite.css';

type HintItem = {
  id: string;
  title: string;
  body: string;
  availableFrom: string;
};

type AnswerCheckItem = {
  id: string;
  title: string;
  body: string;
  answer: string;
};

type PuzzleEventContent = {
  siteTitle: string;
  siteDescription: string;
  hints: HintItem[];
  answerChecks: AnswerCheckItem[];
};

type JudgeState = 'idle' | 'correct' | 'incorrect';

const content = puzzleEventContent as PuzzleEventContent;

const normalizeText = (value: string): string =>
  value
    .normalize('NFKC')
    .trim()
    .replace(/\s+/g, '')
    .toLowerCase();

const formatDateTime = (value: string): string =>
  new Date(value).toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

const MysteryEventSite: React.FC = () => {
  const [now, setNow] = useState<Date>(new Date());
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [judgeStates, setJudgeStates] = useState<Record<string, JudgeState>>({});

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  const sortedHints = useMemo(
    () => [...content.hints].sort((a, b) => new Date(a.availableFrom).getTime() - new Date(b.availableFrom).getTime()),
    []
  );

  const handleJudge = (item: AnswerCheckItem) => {
    const inputValue = answers[item.id] ?? '';
    const isCorrect = normalizeText(inputValue) === normalizeText(item.answer);
    setJudgeStates((prev) => ({
      ...prev,
      [item.id]: isCorrect ? 'correct' : 'incorrect',
    }));
  };

  return (
    <Box className="mystery-site-root">
      <Box className="mystery-site-shape mystery-site-shape-left" />
      <Box className="mystery-site-shape mystery-site-shape-right" />

      <Paper elevation={0} className="mystery-site-panel">
        <Typography className="mystery-site-kicker">謎解き2026特設ページ</Typography>
        <Typography className="mystery-site-title">{content.siteTitle}</Typography>
        <Typography className="mystery-site-description">{content.siteDescription}</Typography>

        <Box className="mystery-site-section">
          <Typography className="mystery-site-section-title">ヒント一覧</Typography>
          <Stack spacing={2}>
            {sortedHints.map((hint) => {
              const openTime = new Date(hint.availableFrom);
              const isVisible = now.getTime() >= openTime.getTime();

              return (
                <Paper key={hint.id} elevation={0} className="mystery-site-card">
                  <Typography className="mystery-site-card-title">{hint.title}</Typography>
                  <Typography className="mystery-site-open-at">公開時刻: {formatDateTime(hint.availableFrom)}</Typography>
                  {isVisible ? (
                    <Typography className="mystery-site-card-body">{hint.body}</Typography>
                  ) : (
                    <Alert severity="info" className="mystery-site-alert">
                      このヒントはまだ公開されていません。
                    </Alert>
                  )}
                </Paper>
              );
            })}
          </Stack>
        </Box>

        <Box className="mystery-site-section">
          <Typography className="mystery-site-section-title">答えの確認</Typography>
          <Stack spacing={2}>
            {content.answerChecks.map((item) => {
              const judgeState = judgeStates[item.id] ?? 'idle';
              return (
                <Paper key={item.id} elevation={0} className="mystery-site-card">
                  <Typography className="mystery-site-card-title">{item.title}</Typography>
                  {/*<Typography className="mystery-site-card-body">{item.body}</Typography>*/}

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} className="mystery-site-form-row">
                    <TextField
                      fullWidth
                      label="答えを入力"
                      value={answers[item.id] ?? ''}
                      onChange={(event) =>
                        setAnswers((prev) => ({
                          ...prev,
                          [item.id]: event.target.value,
                        }))
                      }
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          handleJudge(item);
                        }
                      }}
                    />
                    <Button
                      variant="contained"
                      className="mystery-site-judge-button"
                      disabled={!(answers[item.id] ?? '').trim()}
                      onClick={() => handleJudge(item)}
                    >
                      判定する
                    </Button>
                  </Stack>

                  {judgeState === 'correct' && (
                    <Alert severity="success" className="mystery-site-alert">
                      正解です。次のミッションへ進んでください。
                    </Alert>
                  )}
                  {judgeState === 'incorrect' && (
                    <Alert severity="warning" className="mystery-site-alert">
                      不正解です。もう一度挑戦してください。
                    </Alert>
                  )}
                </Paper>
              );
            })}
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default MysteryEventSite;